import { Box, Grid } from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import useDashboardAccount from '../../../../../hooks/useDashboardAccount';
import resolveDashboardTimezone from './resolveDashboardTimezone';
import useMetricsDailyStats, { OnlyFansDailyStats, OnlyFansPayout } from '../../../../../hooks/useMetricsDailyStats';
import useMetricsDailyStatsEarningsAll from '../../../../../hooks/useMetricsDailyStatsEarningsAll';
import { OnlyFansSubscriber } from '../../../../../hooks/useSubscriber';
import KeyMetricsCards from './performanceStats/KeyMetricsCards';
import PerformerRankingChart from './performanceStats/PerformerRankingChart';
import RevenueBreakdownCard from './performanceStats/RevenueBreakdownCard';
import RevenueChart from './performanceStats/RevenueChart';
import SubscriberGrowthChart from './performanceStats/SubscriberGrowthChart';
import SubscribersStatusCard from './performanceStats/SubscribersStatusCard';
import VisitorTrendsChart from './performanceStats/VisitorTrendsChart';

export interface DashboardProps {
    subscriber: OnlyFansSubscriber;
    data: OnlyFansDailyStats[] | undefined;
    latestEarnings?: OnlyFansPayout.PayoutsStats | null;
    isLatestEarningsLoading?: boolean;
    amountType: 'gross' | 'net';
    handleSetAmountType: (newValue: 'gross' | 'net') => void;
    isLoading: boolean;
}

const SextforceMetricsAccountValueStats = ({ subscriber }: { subscriber: OnlyFansSubscriber }) => {
    const { dashboardAccount } = useDashboardAccount(true);

    const [timezone, setTimezone] = useState<string>(resolveDashboardTimezone(dashboardAccount));

    useEffect(() => {
        if (dashboardAccount && dashboardAccount.timezone) {
            setTimezone(dashboardAccount.timezone);
        }
    }, [dashboardAccount]);

    const [amountType, setAmountType] = useState<'gross' | 'net'>(localStorage.getItem('showEarningsAsGross') === 'true' ? 'gross' : 'net');

    const handleSetAmountType = (newValue: 'gross' | 'net') => {
        setAmountType(newValue);
        localStorage.setItem('showEarningsAsGross', newValue === 'gross' ? 'true' : 'false');
    };

    const startDate: Date = moment().tz(timezone).subtract(1, 'month').startOf('day').toDate();
    const endDate: Date = moment().tz(timezone).endOf('day').toDate();

    const { data: dailyStats, isLoading: isDailyStatsLoading } = useMetricsDailyStats(startDate, endDate, timezone);
    const { data: dailyStatsEarnings, isLoading: isDailyStatsEarningsLoading } = useMetricsDailyStatsEarningsAll('latest');

    return (
        <Box>
            <Grid container flexGrow={1} spacing={2} sx={{ padding: 0 }}>
                {/* Key Metrics */}
                <Grid item xs={12}>
                    <KeyMetricsCards
                        subscriber={subscriber}
                        data={dailyStats}
                        isLoading={isDailyStatsLoading}
                        latestEarnings={dailyStatsEarnings}
                        isLatestEarningsLoading={isDailyStatsEarningsLoading}
                        amountType={amountType}
                        handleSetAmountType={handleSetAmountType}
                    />
                </Grid>

                {/* Revenue Chart */}
                <Grid item xs={12}>
                    <RevenueChart
                        subscriber={subscriber}
                        data={dailyStats}
                        latestEarnings={dailyStatsEarnings}
                        isLatestEarningsLoading={isDailyStatsEarningsLoading}
                        isLoading={isDailyStatsLoading}
                        amountType={amountType}
                        handleSetAmountType={handleSetAmountType}
                    />
                </Grid>

                {/* Subscriber Status & Revenue Breakdown */}
                <Grid item xs={12} md={6}>
                    <SubscribersStatusCard
                        subscriber={subscriber}
                        data={dailyStats}
                        isLoading={isDailyStatsLoading}
                        amountType={amountType}
                        handleSetAmountType={handleSetAmountType}
                    />
                </Grid>

                {/* Revenue Breakdown */}
                <Grid item xs={12} md={6}>
                    <RevenueBreakdownCard
                        subscriber={subscriber}
                        data={dailyStats}
                        isLoading={isDailyStatsLoading}
                        latestEarnings={dailyStatsEarnings}
                        isLatestEarningsLoading={isDailyStatsEarningsLoading}
                        amountType={amountType}
                        handleSetAmountType={handleSetAmountType}
                    />
                </Grid>

                {/* Performer Ranking */}
                <Grid item xs={12}>
                    <PerformerRankingChart
                        subscriber={subscriber}
                        data={dailyStats}
                        isLoading={isDailyStatsLoading}
                        amountType={amountType}
                        handleSetAmountType={handleSetAmountType}
                    />
                </Grid>

                {/* Visitor Trends */}
                <Grid item xs={12}>
                    <VisitorTrendsChart
                        subscriber={subscriber}
                        data={dailyStats}
                        isLoading={isDailyStatsLoading}
                        amountType={amountType}
                        handleSetAmountType={handleSetAmountType}
                    />
                </Grid>

                {/* Subscriber Growth */}
                <Grid item xs={12}>
                    <SubscriberGrowthChart
                        subscriber={subscriber}
                        data={dailyStats}
                        isLoading={isDailyStatsLoading}
                        amountType={amountType}
                        handleSetAmountType={handleSetAmountType}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default SextforceMetricsAccountValueStats;
